import React, { useState, useEffect } from 'react';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { generateWindowFunctionSql, WindowFunctionConfig } from '../utils/sqlGenerator';

interface WindowFunctionBuilderProps {
  columnName: string;
  onSqlGenerate: (sql: string) => void;
}

const WINDOW_FUNCTIONS = [
  {
    value: 'ROW_NUMBER',
    label: 'ROW_NUMBER - Sequential numbering',
    description: 'Assign unique sequential numbers within partition',
    needsColumn: false,
  },
  {
    value: 'RANK',
    label: 'RANK - Ranking with gaps',
    description: 'Assign rank with gaps for ties (1, 2, 2, 4, ...)',
    needsColumn: false,
  },
  {
    value: 'DENSE_RANK',
    label: 'DENSE_RANK - Ranking without gaps',
    description: 'Assign rank without gaps (1, 2, 2, 3, ...)',
    needsColumn: false,
  },
  {
    value: 'LAG',
    label: 'LAG - Previous row value',
    description: 'Access value from previous row',
    needsColumn: true,
  },
  {
    value: 'LEAD',
    label: 'LEAD - Next row value',
    description: 'Access value from next row',
    needsColumn: true,
  },
  {
    value: 'FIRST_VALUE',
    label: 'FIRST_VALUE - First value in partition',
    description: 'Get first value in the partition',
    needsColumn: true,
  },
  {
    value: 'LAST_VALUE',
    label: 'LAST_VALUE - Last value in partition',
    description: 'Get last value in the partition',
    needsColumn: true,
  },
];

export const WindowFunctionBuilder: React.FC<WindowFunctionBuilderProps> = ({
  columnName,
  onSqlGenerate,
}) => {
  const [selectedFunction, setSelectedFunction] = useState('ROW_NUMBER');
  const [partitionBy, setPartitionBy] = useState<string>('');
  const [orderBy, setOrderBy] = useState<string>(columnName);
  const [offset, setOffset] = useState<number>(1);
  const [ascending, setAscending] = useState<boolean>(true);

  const currentFunction = WINDOW_FUNCTIONS.find(f => f.value === selectedFunction);

  useEffect(() => {
    const config: WindowFunctionConfig = {
      functionName: selectedFunction,
      partitionBy: partitionBy ? partitionBy.split(',').map(s => s.trim()) : [],
      orderBy: orderBy ? orderBy.split(',').map(s => s.trim()) : [],
      offset,
      ascending,
    };

    const sql = generateWindowFunctionSql(columnName, config);
    onSqlGenerate(sql);
  }, [selectedFunction, partitionBy, orderBy, offset, ascending, columnName, onSqlGenerate]);

  return (
    <div className="space-y-4">
      <div>
        <Label>Window Function</Label>
        <Select value={selectedFunction} onValueChange={setSelectedFunction}>
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WINDOW_FUNCTIONS.map((func) => (
              <SelectItem key={func.value} value={func.value}>
                {func.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {currentFunction && (
          <div className="text-xs text-muted-foreground mt-1">
            {currentFunction.description}
          </div>
        )}
      </div>

      {(selectedFunction === 'LAG' || selectedFunction === 'LEAD') && (
        <div>
          <Label>Offset (rows to look back/forward)</Label>
          <Input
            type="number"
            min={1}
            value={offset}
            onChange={(e) => setOffset(parseInt(e.target.value) || 1)}
          />
        </div>
      )}

      <div>
        <Label>PARTITION BY (optional)</Label>
        <Input
          placeholder="column1, column2 (comma-separated)"
          value={partitionBy}
          onChange={(e) => setPartitionBy(e.target.value)}
        />
        <div className="text-xs text-muted-foreground mt-1">
          Group data by these columns before applying the function
        </div>
      </div>

      <div>
        <Label>ORDER BY (required)</Label>
        <Input
          placeholder="column1, column2 (comma-separated)"
          value={orderBy}
          onChange={(e) => setOrderBy(e.target.value)}
        />
        <div className="text-xs text-muted-foreground mt-1">
          Order rows within each partition
        </div>
      </div>

      <div className="flex items-center gap-3">
        <Switch
          id="ascending"
          checked={ascending}
          onCheckedChange={setAscending}
        />
        <Label htmlFor="ascending" className="cursor-pointer">
          Ascending order
        </Label>
      </div>

      <div className="text-sm text-muted-foreground">
        <strong>Examples:</strong>
        <ul className="list-disc list-inside mt-1 space-y-1">
          <li>ROW_NUMBER: Assign row numbers 1, 2, 3, ...</li>
          <li>RANK: Rank with ties: 1, 2, 2, 4, 5, ...</li>
          <li>LAG: Get value from N rows before current row</li>
          <li>PARTITION BY: Reset numbering for each group</li>
        </ul>
      </div>

      <div className="bg-muted p-3 rounded-md text-sm">
        <strong>Example Query:</strong>
        <div className="mt-1 font-mono text-xs">
          ROW_NUMBER() OVER (<br />
          &nbsp;&nbsp;PARTITION BY category<br />
          &nbsp;&nbsp;ORDER BY sales DESC<br />
          )
        </div>
        <div className="mt-2">
          This assigns row numbers within each category, ordered by sales
        </div>
      </div>
    </div>
  );
};
